import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { 
  Facebook, 
  Instagram, 
  Phone, 
  Mail, 
  MapPin, 
  Shield, 
  Leaf, 
  ArrowRight, 
  Heart, 
  Globe, 
  Truck, 
  Award, 
  CheckCircle,
  Twitter,
  Linkedin,
  Youtube
} from "lucide-react";

const Footer = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const quickLinks = [
    { name: "Home", path: "/", icon: <Globe className="w-4 h-4" /> },
    { name: "About Us", path: "/about", icon: <Heart className="w-4 h-4" /> },
    { name: "Products", path: "/products", icon: <Leaf className="w-4 h-4" /> },
    { name: "Get Quote", path: "/devis", icon: <Award className="w-4 h-4" /> },
    { name: "Contact", path: "/contact", icon: <Phone className="w-4 h-4" /> },
  ];

  const services = [
    "Custom Branding",
    "Bulk Orders",
    "Fast Delivery",
    "Quality Guarantee",
    "Eco-Friendly Materials"
  ];

  const socialLinks = [
    { 
      name: "Facebook", 
      url: "https://www.facebook.com/profile.php?id=61572152653497", 
      icon: <Facebook className="w-5 h-5" />,
      color: "hover:text-blue-600"
    },
    { 
      name: "Instagram", 
      url: "https://www.instagram.com/greenecobgas?igsh=bmJwMWd0ODU2YzFx", 
      icon: <Instagram className="w-5 h-5" />,
      color: "hover:text-pink-600"
    },
    // { 
    //   name: "Twitter", 
    //   url: "#", 
    //   icon: <Twitter className="w-5 h-5" />,
    //   color: "hover:text-blue-400"
    // },
    // { 
    //   name: "LinkedIn", 
    //   url: "#", 
    //   icon: <Linkedin className="w-5 h-5" />,
    //   color: "hover:text-blue-700"
    // },
    // { 
    //   name: "YouTube", 
    //   url: "#", 
    //   icon: <Youtube className="w-5 h-5" />,
    //   color: "hover:text-red-600"
    // }
  ];

  const features = [
    { icon: <Shield className="w-6 h-6" />, text: "Secure & Trusted" },
    { icon: <Leaf className="w-6 h-6" />, text: "100% Eco-Friendly" },
    { icon: <Truck className="w-6 h-6" />, text: "Fast Delivery" },
    { icon: <Award className="w-6 h-6" />, text: "Quality Guaranteed" }
  ];

  return (
    <footer className="relative overflow-hidden">
      {/* Beautiful Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-green-700 via-emerald-600 to-teal-600"></div>
      
      {/* Accent Line */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-300 via-emerald-300 to-teal-300"></div>
      
      {/* Floating Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-10 left-10 w-32 h-32 bg-green-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse"></div>
        <div className="absolute top-20 right-20 w-24 h-24 bg-emerald-200 rounded-full mix-blend-multiply filter blur-xl opacity-25 animate-pulse animation-delay-1000"></div>
        <div className="absolute bottom-10 right-10 w-40 h-40 bg-teal-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000"></div>
        <div className="absolute bottom-20 left-20 w-28 h-28 bg-green-300 rounded-full mix-blend-multiply filter blur-xl opacity-15 animate-pulse animation-delay-3000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-24 bg-emerald-300 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse animation-delay-4000"></div>
      </div>
      
      {/* Subtle Pattern Overlay */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 0%, transparent 50%),
                           radial-gradient(circle at 75% 75%, rgba(255,255,255,0.1) 0%, transparent 50%)`,
          backgroundSize: '100px 100px'
        }}></div>
      </div>

      <div className="relative">
        {/* Main Footer Content */}
        <div className="container mx-auto px-4 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Brand Section */}
            <div className={`space-y-6 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div>
                <h2 className="text-3xl font-bold text-white mb-4">
                  <span className="bg-gradient-to-r from-green-400 via-emerald-400 to-teal-400 bg-clip-text text-transparent">
                    GREEN ECO
                  </span>
                </h2>
                <p className="text-gray-300 text-lg leading-relaxed">
                  Your trusted partner for sustainable, eco-friendly packaging solutions. 
                  Making a difference, one bag at a time.
                </p>
              </div>

              {/* Features */}
              <div className="space-y-3">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="text-green-400">{feature.icon}</div>
                    <span className="text-gray-300 text-sm">{feature.text}</span>
                  </div>
                ))}
              </div>

              {/* Newsletter */}
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-4 border border-white/20">
                <h4 className="text-white font-semibold mb-3">Stay Updated</h4>
                <p className="text-gray-300 text-sm mb-4">Get the latest eco-friendly news and offers</p>
                <div className="flex space-x-2">
                  <input
                    type="email"
                    placeholder="Your email"
                    className="flex-1 px-3 py-2 bg-white/20 border border-white/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-400 text-sm"
                  />
                  <button className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition-colors duration-300">
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div className={`space-y-6 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <h3 className="text-xl font-bold text-white">Quick Links</h3>
              <ul className="space-y-3">
                {quickLinks.map((link, index) => (
                  <li key={index}>
                    <Link 
                      to={link.path} 
                      className="group flex items-center space-x-3 text-gray-300 hover:text-white transition-colors duration-300"
                    >
                      <div className="text-green-400 group-hover:text-green-300 transition-colors duration-300">
                        {link.icon}
                      </div>
                      <span className="group-hover:translate-x-1 transition-transform duration-300">
                        {link.name}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>

              {/* Services */}
              <div>
                <h4 className="text-lg font-semibold text-white mb-4">Our Services</h4>
                <ul className="space-y-2">
                  {services.map((service, index) => (
                    <li key={index} className="flex items-center space-x-2 text-gray-300">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="text-sm">{service}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Contact Information */}
            <div className={`space-y-6 transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <h3 className="text-xl font-bold text-white">Contact Us</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3 group">
                  <div className="bg-green-500/20 p-2 rounded-lg group-hover:bg-green-500/30 transition-colors duration-300">
                    <Phone className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Phone</p>
                    <p className="text-gray-300">+1(303) 872-0109</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 group">
                  <div className="bg-green-500/20 p-2 rounded-lg group-hover:bg-green-500/30 transition-colors duration-300">
                    <Mail className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Email</p>
                    <p className="text-gray-300">Info@greenecobagsusa.com</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 group">
                  <div className="bg-green-500/20 p-2 rounded-lg group-hover:bg-green-500/30 transition-colors duration-300">
                    <MapPin className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Address</p>
                    <p className="text-gray-300">
                      1905 Sherman St Suite 200<br />
                      Denver, CO 80203, United States
                    </p>
                  </div>
                </div>
              </div>

              {/* Business Hours */}
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-4 border border-white/20">
                <h4 className="text-white font-semibold mb-3">Business Hours</h4>
                <div className="space-y-1 text-sm text-gray-300">
                  <p>Monday - Friday: 9:00 AM - 6:00 PM</p>
                  <p>Saturday: 10:00 AM - 4:00 PM</p>
                  <p>Sunday: Closed</p>
                </div>
              </div>
            </div>

            {/* Social Media & Legal */}
            <div className={`space-y-6 transition-all duration-1000 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <h3 className="text-xl font-bold text-white">Follow Us</h3>
              <div className="grid grid-cols-2 gap-3">
                {socialLinks.map((social, index) => (
                  <Link
                    key={index}
                    target="_blank"
                    to={social.url}
                    className={`group flex items-center space-x-2 p-3 bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300 ${social.color}`}
                  >
                    <div className="group-hover:scale-110 transition-transform duration-300">
                      {social.icon}
                    </div>
                    <span className="text-sm font-medium text-gray-300 group-hover:text-white">
                      {social.name}
                    </span>
                  </Link>
                ))}
              </div>

              {/* Legal Links */}
              <div>
                <h4 className="text-lg font-semibold text-white mb-4">Legal</h4>
                <div className="space-y-2">
                  <Link
                    to="/privacy-policy"
                    className="block text-gray-300 hover:text-white transition-colors duration-300 flex items-center space-x-2"
                  >
                    <Shield className="w-4 h-4" />
                    <span>Privacy Policy</span>
                  </Link>
                  <Link
                    to="/terms"
                    className="block text-gray-300 hover:text-white transition-colors duration-300 flex items-center space-x-2"
                  >
                    <Shield className="w-4 h-4" />
                    <span>Terms of Service</span>
                  </Link>
                  <Link
                    to="/cookies"
                    className="block text-gray-300 hover:text-white transition-colors duration-300 flex items-center space-x-2"
                  >
                    <Shield className="w-4 h-4" />
                    <span>Cookie Policy</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/20 bg-black/20 backdrop-blur-lg">
          <div className="container mx-auto px-4 py-6">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="flex items-center space-x-2 text-gray-300">
                <Heart className="w-4 h-4 text-red-400" />
                <span>Made with love for our planet</span>
              </div>
              
              <p className="text-gray-300 text-sm">
                © {new Date().getFullYear()} GREEN ECO. All rights reserved.
              </p>
              
              <div className="flex items-center space-x-4 text-sm text-gray-300">
                <span>🌱 Eco-Friendly</span>
                <span>♻️ Sustainable</span>
                <span>🌍 Green Future</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        .animation-delay-1000 {
          animation-delay: 1s;
        }

        .animation-delay-2000 {
          animation-delay: 2s;
        }

        .animation-delay-3000 {
          animation-delay: 3s;
        }

        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </footer>
  );
};

export default Footer;
